import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useData } from '../store.jsx'
import { PageHeader, Stat, Panel, Amount } from '../components/ui.jsx'
import { NetWorthChart, IncomeExpenseChart, RangePicker, useRange, BarList } from '../components/charts.jsx'
import { netWorth, monthlyTotals, snapshotSeries, upcoming, entries } from '../lib/finance.js'
import { money, todayISO, dateLabel, monthLabel } from '../lib/format.js'
import { ACCOUNT_CATEGORIES } from '../lib/constants.js'

export default function Dashboard() {
  const data = useData()
  const today = todayISO()
  const month = today.slice(0, 7)
  const nw = useMemo(() => netWorth(data, today), [data, today])
  const series = useMemo(() => snapshotSeries(data.snapshots), [data.snapshots])
  const { range, setRange, filtered } = useRange(series)
  const months = useMemo(() => monthlyTotals(data.transactions).slice(-12), [data.transactions])
  const thisMonth = months.find((m) => m.month === month) || { income: 0, expense: 0 }
  const due = useMemo(() => upcoming(data, today, 14), [data, today])

  const monthAgo = series.filter((s) => s.date <= shift(today, -30)).at(-1)
  const change = monthAgo ? nw.net - monthAgo.net : null

  const allocation = ACCOUNT_CATEGORIES.map((c) => ({ label: c.label, value: nw.byCategory[c.id] || 0 })).filter((x) => x.value)
  const recent = entries(data.transactions).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6)

  return (
    <>
      <PageHeader title="Dashboard" subtitle={dateLabel(today)} />
      <div className="row g-3 mb-3">
        <div className="col-6 col-xl-3"><Stat label="Net worth" value={money(nw.net)} tone={nw.net < 0 ? 'neg' : ''}
          hint={change != null ? <span className={change >= 0 ? 'pos' : 'neg'}>{money(change, { sign: true })} vs 30 days ago</span> : 'Assets − debt'} /></div>
        <div className="col-6 col-xl-3"><Stat label="Assets" value={money(nw.assets)} hint={`${entries(data.accounts).length} accounts`} /></div>
        <div className="col-6 col-xl-3"><Stat label="Debt" value={money(nw.debt)} hint="Estimated with daily interest" /></div>
        <div className="col-6 col-xl-3"><Stat label={`${monthLabel(month, 'short')} balance`} value={money(thisMonth.income - thisMonth.expense, { sign: true })}
          tone={thisMonth.income - thisMonth.expense < 0 ? 'neg' : 'pos'} hint={`In ${money(thisMonth.income)} · Out ${money(thisMonth.expense)}`} /></div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-8">
          <Panel title="Net worth over time" action={<RangePicker value={range} onChange={setRange} />}>
            <NetWorthChart data={filtered} />
          </Panel>
        </div>
        <div className="col-lg-4">
          <Panel title="Allocation" className="h-100" action={<Link to="/accounts" className="small">Balances</Link>}>
            <BarList items={allocation} total={nw.assets} />
          </Panel>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-8">
          <Panel title="Income vs outcome · last 12 months" action={<Link to="/transactions" className="small">All transactions</Link>}>
            <IncomeExpenseChart data={months} />
          </Panel>
        </div>
        <div className="col-lg-4 d-flex flex-column gap-3">
          <Panel title="Due in the next 14 days" action={<Link to="/recurring" className="small">Manage</Link>}>
            {due.length ? (
              <table className="table table-sm"><tbody>
                {due.map((r) => (
                  <tr key={r.id + r.due}><td className="text-body-secondary small">{dateLabel(r.due).replace(/ \d{4}$/, '')}</td><td>{r.name}</td>
                    <td className="num">{r.amount != null ? <Amount value={r.amount} type={r.type} /> : '–'}</td></tr>
                ))}
              </tbody></table>
            ) : <div className="empty py-3">Nothing due.</div>}
          </Panel>
          <Panel title="Recent">
            {recent.length ? (
              <table className="table table-sm"><tbody>
                {recent.map((t) => (
                  <tr key={t.id}><td className="text-body-secondary small">{dateLabel(t.date).replace(/ \d{4}$/, '')}</td>
                    <td className="text-truncate" style={{ maxWidth: 140 }}>{t.note || t.category}</td><td className="num"><Amount value={t.amount} type={t.type} /></td></tr>
                ))}
              </tbody></table>
            ) : <div className="empty py-3">No transactions yet.</div>}
          </Panel>
        </div>
      </div>
    </>
  )
}

function shift(iso, days) {
  const d = new Date(iso); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10)
}
